import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
  NO_QUALIFICATION_FILTERS,
  type LeadQualificationFilterValues,
} from '../qualificationFilters';

/**
 * Persisted view-mode preference for the Leads list (CLAUDE.md §2.3).
 *
 * Search / status filter live in the URL-bound page state and stay out of the
 * store — those are server-driven query params, not durable user prefs.
 *
 * `showArchived` is a transient session toggle — it doesn't persist, so a
 * fresh page load defaults back to the active list (the safe default).
 */
export type LeadsViewMode = 'table' | 'list' | 'cards' | 'grouped';

export const LEADS_VIEW_MODES: LeadsViewMode[] = ['table', 'list', 'cards', 'grouped'];

interface LeadsUiState {
  view: LeadsViewMode;
  setView: (view: LeadsViewMode) => void;
  showArchived: boolean;
  setShowArchived: (showArchived: boolean) => void;
  /** Current DRF `OrderingFilter` value (e.g. `-created`). Session-only. */
  ordering: string | undefined;
  setOrdering: (ordering: string | undefined) => void;
  /** Server-side "owner = me" filter (Lead has `owner`). Session-only. */
  mine: boolean;
  setMine: (mine: boolean) => void;
  /** Persisted filters — survive reloads (see partialize). */
  status: string;
  setStatus: (status: string) => void;
  /** Owner (salesperson) filter — a User id as a string; '' = all. */
  owner: string;
  setOwner: (owner: string) => void;
  tag: string;
  setTag: (tag: string) => void;
  createdFrom: string;
  setCreatedFrom: (v: string) => void;
  createdTo: string;
  setCreatedTo: (v: string) => void;
  /** Qualification filters — fit, segment, company profile (ADR-108). */
  qualification: LeadQualificationFilterValues;
  setQualification: (v: LeadQualificationFilterValues) => void;
}

export const useLeadsUi = create<LeadsUiState>()(
  persist(
    (set) => ({
      view: 'table',
      setView: (view) => set({ view }),
      showArchived: false,
      setShowArchived: (showArchived) => set({ showArchived }),
      ordering: undefined,
      setOrdering: (ordering) => set({ ordering }),
      mine: false,
      setMine: (mine) => set({ mine }),
      status: 'all',
      setStatus: (status) => set({ status }),
      owner: '',
      setOwner: (owner) => set({ owner }),
      tag: 'all',
      setTag: (tag) => set({ tag }),
      createdFrom: '',
      setCreatedFrom: (createdFrom) => set({ createdFrom }),
      createdTo: '',
      setCreatedTo: (createdTo) => set({ createdTo }),
      qualification: NO_QUALIFICATION_FILTERS,
      setQualification: (qualification) => set({ qualification }),
    }),
    {
      name: 'crm.leads:ui',
      // View mode + the explicit filters persist across reloads. The archived
      // toggle, ordering, and `mine` stay ephemeral so refreshes return to the
      // active list with no implicit filtering.
      partialize: (s) => ({
        view: s.view,
        status: s.status,
        owner: s.owner,
        tag: s.tag,
        createdFrom: s.createdFrom,
        createdTo: s.createdTo,
        qualification: s.qualification,
      }),
    },
  ),
);
